import { useMutation, useQuery } from '@tanstack/react-query'
import type { ShareLinkAccessOutput, ShareLinkPublicMeta } from '@terafile/shared'
import { type FormEvent, useState } from 'react'
import { useParams } from 'react-router-dom'
import { GuestFileViewer } from '../components/GuestFileViewer.js'
import {
  SHARE_LINK_UNAVAILABLE,
  SUBMIT_ERROR_FALLBACK,
  USER_MESSAGES,
  toUserMessage,
} from '../lib/error-copy.js'
import { getVisitorId } from '../lib/fingerprint.js'
import { LOADING, SHARE_PERMISSION_LABEL } from '../lib/labels.js'
import { type GuestApiError, accessShareLink, getShareLinkMeta } from '../lib/share-links-api.js'

/**
 * 共有URLゲスト着地ページ。
 *
 * フロー:
 * 1. GET /share/:token でメタ取得（期限切れ/失効はここで判別）
 * 2. emailRequired=true → メアド入力フォーム
 * 3. POST /share/:token/access でゲスト JWT を取得
 * 4. ファイル一覧へ遷移（GuestFileViewer）
 */
export function SharePage() {
  const { token } = useParams<{ token: string }>()
  const [session, setSession] = useState<ShareLinkAccessOutput | null>(null)

  const meta = useQuery<ShareLinkPublicMeta, GuestApiError>({
    queryKey: ['share-meta', token],
    queryFn: () => getShareLinkMeta(token ?? ''),
    enabled: !!token,
    retry: false,
  })

  if (meta.isLoading) {
    return (
      <CenterLayout>
        <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>
      </CenterLayout>
    )
  }
  if (meta.error) {
    return <ShareErrorView error={meta.error} />
  }
  if (!meta.data || !token) return null

  if (session) {
    return <GuestFileViewer token={token} session={session} />
  }

  return <AccessForm token={token} meta={meta.data} onAccess={setSession} />
}

function ShareErrorView({ error }: { error: GuestApiError }) {
  // 失効・期限切れはユーザー視点では同じ「現在使えない」状態。
  // 差を見せる必要が無いので統一文言にする (issue #38)。
  let title = 'この共有URLは現在ご利用いただけません'
  let body: string = SHARE_LINK_UNAVAILABLE
  if (error.status === 404) {
    title = '共有URLが見つかりません'
    body = USER_MESSAGES.shareLinkNotFound
  } else if (error.code !== 'SHARE_LINK_REVOKED' && error.code !== 'SHARE_LINK_EXPIRED') {
    // 想定外のエラーコードはサーバ由来のメッセージを残す（既にユーザー向けに整形されている前提）
    body = toUserMessage(error, SHARE_LINK_UNAVAILABLE)
  }
  return (
    <CenterLayout>
      <div className="text-center space-y-3 max-w-md">
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground">{body}</p>
      </div>
    </CenterLayout>
  )
}

function AccessForm({
  token,
  meta,
  onAccess,
}: {
  token: string
  meta: ShareLinkPublicMeta
  onAccess: (session: ShareLinkAccessOutput) => void
}) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      // FingerprintJS で算出した visitor id を匿名識別子の合成に使う（F-LOG-06）
      const visitorId = await getVisitorId()
      return accessShareLink(token, {
        ...(meta.emailRequired && email ? { email } : {}),
        visitorId,
      })
    },
    onSuccess: onAccess,
    onError: (err) => {
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (meta.emailRequired && !email) {
      setError('メールアドレスを入力してください')
      return
    }
    mutation.mutate()
  }

  return (
    <CenterLayout>
      <div className="w-full max-w-md space-y-4">
        <div className="text-center">
          <h1 className="text-xl font-semibold">共有URLにアクセス</h1>
          <p className="mt-1 text-sm text-muted-foreground">プロジェクト「{meta.projectName}」</p>
          <p className="mt-1 text-xs text-muted-foreground">
            権限: {permissionLabel(meta.permission)} · ファイル {meta.fileCount} 件
          </p>
        </div>
        <form onSubmit={onSubmit} className="space-y-3">
          {meta.emailRequired && (
            <div>
              <label htmlFor="email" className="text-sm font-medium">
                メールアドレス
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-border rounded-md bg-background"
                placeholder="you@example.com"
              />
            </div>
          )}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={mutation.isPending}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium disabled:opacity-50"
          >
            {mutation.isPending ? LOADING.sending : 'アクセスする'}
          </button>
        </form>
      </div>
    </CenterLayout>
  )
}

function CenterLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      {children}
    </div>
  )
}

function permissionLabel(p: ShareLinkPublicMeta['permission']): string {
  return SHARE_PERMISSION_LABEL[p]
}

// visitorId は `apps/app/src/lib/fingerprint.ts` の FingerprintJS 統合版を使用する。
// 旧来あった localStorage UUID ベースの簡易実装は廃止（暫定テナントと同じ識別系に統一）。
